import { useAuthStore } from '@/lib/stores/authStore';

const API_BASE_URL = import.meta.env.VITE_AUTH_API_URL;

function getAuthHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export interface GeneratePrescriptionRequest {
  patientName: string;
  patientPhone: string;
  patientAge?: number;
  patientGender?: string;
  patientDateOfBirth?: string;
  patientAddress?: string;
  patientIdentifier?: string;
  practitionerId: string;
  doctorName?: string;
  encounterId: string;
  consultationDate?: string;
  vitals?: {
    height?: number;
    weight?: number;
    temperature?: number;
    bloodPressure?: string;
    heartRate?: number;
    respiratoryRate?: number;
    oxygenSaturation?: number;
  };
  chiefComplaint?: string;
  diagnosis?: string;
  medications: {
    medication: string;
    dosage?: string;
    frequency?: string;
    timing?: string[];
    duration?: string;
    // Ayurveda Medicines table extras (form/anupana-vehicle/notes) — optional,
    // rendered as extra columns by the backend PDF service when present.
    medicineType?: string;
    relationWithMeal?: string;
    additionalInfo?: string;
  }[];
  investigations?: string;
  doctorNotes?: string;
}

export interface GeneratePrescriptionResponse {
  success: boolean;
  data: {
    filePath: string;
    fileName: string;
    patientPhone: string;
  };
}

export interface SendPrescriptionRequest {
  filePath: string;
  patientPhone: string;
}

export interface SendPrescriptionResponse {
  success: boolean;
  data: {
    message: string;
  };
}

export async function generatePrescription(
  data: GeneratePrescriptionRequest
): Promise<GeneratePrescriptionResponse> {
  const response = await fetch(`${API_BASE_URL}/api/prescription/generate`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to generate prescription: ${response.status}`);
  }

  return response.json();
}

export async function sendPrescription(
  data: SendPrescriptionRequest
): Promise<SendPrescriptionResponse> {
  const response = await fetch(`${API_BASE_URL}/api/prescription/send`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to send prescription: ${response.status}`);
  }

  return response.json();
}
